import React, {
  useEffect,
  useMemo,
  Dispatch,
  SetStateAction,
  MutableRefObject,
} from 'react';
import { Checkbox } from 'tea-component';
import CardSlotV2 from '@/pages/intelligence/components/CardSlotV2';
import DynamicColTable from './DynamicColTable';
import { getLocale } from 'umi';
import CardStats from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Stats/components/CardStats';
import Visualis from './Visualis';
import ShowAllTrueData from './ShowAllTrueData';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { pageCfgType } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_config';
import { getChartDisableType } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_utils';
import classNames from 'classnames';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import { ChartContext } from '@/pages/intelligence/layouts';
// import { MetricsHasPlatFormData } from '../_config';
import { allMetricsOptions } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import { isKpiTrendsHourlyGranularity } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/hourSegmentUtils';

import './index.scss';
import { useShowSumAvgConfig } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/hooks/useShowSumAvgInfo';
import { getTrendsChartGridRightByGranularity } from '@intelligence/v3/utils/echarts';

import type { IPublicTrueData } from '@intelligence/types/topCharts';
import type { IAlineaTrueDataSeries } from '../utils/alineaTrueData';
import type { IMetricExplanationTipsClickHandler } from '@intelligence/v3/components/MetricExplanationTips';
import type {
  IOpinionChartEventData,
  IOpinionChartEventSetting,
  IOpinionEventConfig,
} from '@intelligence/v3/components/OpinionBarAndLineChart/hooks/types';

interface ITrendsVisualTableContentProps extends IPublicTrueData {
  cardTitle?: React.ReactNode;
  subTitle?: React.ReactNode;
  defaultByDeviceVal?: boolean;
  // noDataMessage?: React.ReactNode;
  pageCfg: pageCfgType;
  pageId: string;
  entityType: string;
  gameId: string;
  cardStatus: {
    isLoading?: boolean;
    isError?: boolean;
    isNoData?: boolean;
  };
  pageSortAttr?: {
    currentSortValue: string;
    currentSortMetric: string;
    currentRatioType: number;
  };
  tableData: any[];
  fixedTableData?: any[]; // 固定在首行的内容
  barData?: any;
  pieData?: any;
  lineData?: any;
  stackBarData?: any;
  stackLineData?: any;
  stackPieData?: any;
  chartType: string;
  setChartType: Function;
  metricSegVal: string;
  setMetricSegVal: Function;
  earliestDay?: string;
  baseReleaseTime?: string;
  earliestPeriod?: string;
  dateType: string;
  fetchParams?: any;
  chartLoading: boolean;
  setChartLoading: Function;
  // visualisMetric?: any;
  visualisTitleNow?: any;
  isStepLine?: boolean;
  marketLineLoading?: boolean;
  ifUseMarketLineLoading?: boolean;
  isCountriesPage?: boolean;
  gameName?: string;
  gameCover?: string;
  startDate?: string;
  endDate?: string;
  calOtherList?: any;
  changeDeviceBtn?: Function;
  isDropdown?: boolean;
  regionsCountries: any;
  sumAvgMaxList: any[];
  visualMetricValue?: string;
  visualMetricValues?: string[];
  alineaTrueDataSeries?: IAlineaTrueDataSeries[];
  setVisualMetricValue?: Dispatch<SetStateAction<string | undefined>>;
  isManuallyTriggerMetricSelectRef?: MutableRefObject<boolean>;
  topListTmpCur?: any;
  showRadio?: boolean;
  radioValue?: string;
  setRadioValue?: Function;
  getTop5DataList?: Function;
  // isPercentage?: boolean;
  ifLineChartLegendOn?: boolean;
  rightDom?: React.ReactNode;
  chartNodataMsg?: string;
  legendSelected?: any;
  className?: string;
  mainSegment?: string;
  trendTitle?: string;
  lineSourceName?: string;
  exportTableCom?: React.ReactNode;
  byDevice?: boolean;
  hasVisualMetricValue?: boolean;
  onChartTypeChange?: (chartType: 'pie' | 'line') => void;
  handleByPlatformChange: (value: boolean) => void;
  byCountry?: boolean;
  handleByCountryChange?: (value: boolean) => void;
  // 是否有databrain校准数据
  hasDatabrainData?: boolean;
  // 是否有 Alinea Calibration 真数
  hasAlineaData?: boolean;
  onMetricExplanationClick?: IMetricExplanationTipsClickHandler;
  enableOpinionEventAnnotation?: boolean;
  controlledEventAnnotation?: {
    eventData: IOpinionChartEventData;
    chartEventSetting: IOpinionChartEventSetting;
    changeEventSetting: (setting: IOpinionChartEventSetting) => void;
    eventLoading?: boolean;
  };
  opinionEventConfig?: IOpinionEventConfig;
}

const BASE_GRID_CONFIG = {
  top: 20,
  bottom: 60,
  left: 40,
  right: 20,
  containLabel: false,
};

const chartIconList = [
  {
    value: 'line',
    icon: 'discount-chart',
    context: 'dbi_line_chart',
    disabled: false,
  },
  {
    value: 'pie',
    icon: 'pie-chart-icon',
    context: 'dbi_pie_chart',
    disabled: false,
  },
];

const TrendsVisualTableContent = (props: ITrendsVisualTableContentProps) => {
  const {
    cardTitle: cardTitleProp,
    // subTitle,
    pageId,
    cardStatus,
    entityType,
    gameId,
    byDevice,
    handleByPlatformChange,
    byCountry,
    handleByCountryChange,
    // noDataMessage,
    pageCfg,
    pageSortAttr,
    // metric 单选框选中值
    visualMetricValue = '',
    visualMetricValues,
    alineaTrueDataSeries,
    setVisualMetricValue,
    isManuallyTriggerMetricSelectRef,
    tableData = [],
    fixedTableData,
    // barData = [],
    pieData = [],
    lineData = [],
    // chartType: chartTypeProp,
    chartType,
    setChartType,
    metricSegVal,
    // setMetricSegVal,
    // earliestDay,
    dateType,
    chartLoading,
    setChartLoading,
    baseReleaseTime,
    earliestPeriod,
    // visualisMetric,
    visualisTitleNow,
    isStepLine,
    isCountriesPage,
    gameName,
    gameCover,
    calOtherList = [],
    // changeDeviceBtn,
    isDropdown,
    regionsCountries,
    // stackBarData,
    stackLineData,
    stackPieData,
    startDate,
    endDate,
    sumAvgMaxList,
    // isPercentage,
    rightDom,
    chartNodataMsg,
    legendSelected,
    className,
    marketLineLoading,
    ifUseMarketLineLoading = false,
    mainSegment,
    trendTitle,
    lineSourceName,
    ifLineChartLegendOn = false,
    exportTableCom,
    hasVisualMetricValue,
    hasDatabrainData,
    hasAlineaData,
    onChartTypeChange,
    onMetricExplanationClick,
    feedBackParams,
    enableOpinionEventAnnotation,
    controlledEventAnnotation,
    opinionEventConfig,
  } = props;

  const isHourlyMode = isKpiTrendsHourlyGranularity(dateType);

  // console.log('TrendsVisualTableContent', props);

  const ChartConsumer = ChartContext.Consumer; // 订阅消费者

  const {
    disableChartType,
    defaultChartType,
    tableMetricList,
    sumavgArr: sumAvgArrPage, // 页面配置里有则用配置里的，否则用指标配置里的
  } = pageCfg || {};

  const metricItem = tableMetricList?.find((t) => {
    return t.value === metricSegVal;
  });

  const locale = getLocale(); // 中英切换方法
  const { formatMessage } = useIntlExtend();
  const { isLoading, isError, isNoData } = cardStatus || {};

  // 获取需要展示 sum、avg 的指标
  const showSumAvgMetrics = useShowSumAvgConfig();

  const metricsCount = useMemo(() => {
    if (!Array.isArray(visualMetricValues)) return 0;
    return visualMetricValues.filter(Boolean).length;
  }, [visualMetricValues]);

  const isLineChartType =
    chartType === ChartType.Line || chartType === ChartType.StackedLine;
  const isDualMetricLineChart = isLineChartType && metricsCount >= 2;

  const chartGridConfig = useMemo(() => {
    // 单y轴
    if (!isDualMetricLineChart) {
      return {
        ...BASE_GRID_CONFIG,
        right: getTrendsChartGridRightByGranularity(40, dateType),
      };
    }

    // 双y轴
    return {
      ...BASE_GRID_CONFIG,
      right: getTrendsChartGridRightByGranularity(50, dateType),
    };
  }, [dateType, isDualMetricLineChart]);

  const chartWrapperClass = useMemo(
    () =>
      classNames('trends-card-line', {
        'trends-card-line--with-gap': isDualMetricLineChart,
      }),
    [isDualMetricLineChart],
  );

  // 是否需要展示 By Platform 按钮
  const showDeviceBtn =
    hasVisualMetricValue &&
    // MetricsHasPlatFormData.find((item) => item.value === visualMetricValue)?.hasByDevice;
    allMetricsOptions.find((item) => item.value === visualMetricValue)
      ?.hasByDevice &&
    // 当选择多个指标时（visualMetricValues长度大于1），不显示By Platform按钮
    (visualMetricValues?.length || 0) <= 1;

  // 是否需要展示 By Country 按钮：Steam 指标（dataSource 为 vginsights / gamalytic / source_steam / alinea）不展示
  // alinea 数据来源同样不提供按国家维度的数据
  const STEAM_DATA_SOURCES = ['vginsights', 'gamalytic', 'source_steam', 'alinea'];
  const currentMetricOption = allMetricsOptions.find((item) => item.value === visualMetricValue);
  const showByCountryBtn =
    hasVisualMetricValue &&
    !STEAM_DATA_SOURCES.includes(currentMetricOption?.dataSource || '') &&
    (visualMetricValues?.length || 0) <= 1;

  const newdisableChartType = getChartDisableType({
    hasDeviceBtn: showDeviceBtn,
    disableChartType,
    isCountriesPage,
  });

  useEffect(() => {
    // 用图标列表过滤掉被禁用的，如果有默认icon则用，否则用非禁用ICON的第一个值
    const activeIconList = chartIconList?.filter((item) => {
      return !newdisableChartType.includes(item?.value);
    });
    const defaultChartItem = activeIconList?.find((t) => {
      return t?.value === defaultChartType;
    });
    // 不要调外面的函数 ，外面的函数可能在判断调用接口
    setChartType(defaultChartItem?.value || activeIconList?.[0]?.value);
  }, [JSON.stringify(newdisableChartType)]);

  const getSumAvgList = (sumavgArr: string[], sumAvgMaxList: any[]) => {
    if (Array.isArray(sumavgArr) && Array.isArray(sumAvgMaxList)) {
      const sumAvgData = sumavgArr
        ?.map((t: string) => {
          return sumAvgMaxList?.find((listItem) => {
            return listItem.date === t;
          });
        })
        .filter((item) => item !== undefined);

      // 不显示 sum 或 svg 的指标置空
      const sumData = sumAvgData?.find((item) => item.date === 'sum');
      if (sumData?.metrics) {
        sumData.metrics = sumData.metrics?.map((item: any) => {
          // 未获取到展示 sum 的指标配置，先展示
          if (!showSumAvgMetrics?.sum?.length) return item;
          // 有配置，根据配置展示

          return showSumAvgMetrics?.sum?.includes(item?.metric) ? item : null;
        });
      }

      const avgData = sumAvgData?.find((item) => item.date === 'avg');
      if (avgData?.metrics) {
        avgData.metrics = avgData.metrics?.map((item: any) => {
          // 未获取到展示 avg 的指标配置，先展示
          if (!showSumAvgMetrics?.avg?.length) return item;
          // 有配置，根据配置展示
          return showSumAvgMetrics?.avg?.includes(item?.metric) ? item : null;
        });
      }

      return sumAvgData;
    }
    return [];
  };

  // 页面配置里有则用配置里的，否则用指标配置里的
  const sumAvgList = getSumAvgList(
    sumAvgArrPage?.length ? sumAvgArrPage : metricItem?.sumavgArr,
    sumAvgMaxList,
  );

  const changeDevice = (value: boolean) => {
    handleByPlatformChange(value);
  };
  const showDeviceBtnEffective = showDeviceBtn && !isHourlyMode;
  const showByCountryBtnEffective = showByCountryBtn && !isHourlyMode;
  const deviceRealVal =
    (byDevice && showDeviceBtnEffective) || (byCountry && showByCountryBtnEffective);

  const showStatusCard = isLoading || isNoData || isError || isDropdown;
  // 根据不同场景返回secondTitleRight
  const bubbleObjCur = useMemo(() => {
    if (isHourlyMode) {
      return {
        secondTitleRight: formatMessage({ id: trendTitle }),
        lineSourceIcon: 'steam',
        lineSourceName,
      };
    }
    switch (pageId) {
      case 'digitalRevenue':
      case 'physicalRevenue':
      case 'gsd': {
        const typeList = ['pc', 'combine', 'console'];
        const lineSourceObj = {
          digitalRevenue: 'mscience',
          physicalRevenue: 'npd',
          gsd: 'gsd',
        };
        const isNewObj = typeList.includes(entityType);
        const secondRigText = isNewObj
          ? visualisTitleNow
          : formatMessage({ id: trendTitle });
        const lineSource =
          isNewObj && !isCountriesPage && !deviceRealVal
            ? lineSourceObj[pageId]
            : '';
        const lineSourceNameText =
          isNewObj && !deviceRealVal && !isCountriesPage ? ' ' : lineSourceName;
        return {
          secondTitleRight: secondRigText,
          lineSourceIcon: lineSource,
          lineSourceName: lineSourceNameText,
        };
      }
      case 'pcu': {
        const typeList = ['pc'];
        const isNewObj = typeList.includes(entityType);
        const secondRigText = isNewObj
          ? ' '
          : formatMessage({ id: trendTitle });
        const lineSource = '';
        const lineSourceNameText = lineSourceName;
        return {
          secondTitleRight: secondRigText,
          lineSourceIcon: lineSource,
          lineSourceName: lineSourceNameText,
        };
      }
      default:
        return {
          secondTitleRight: formatMessage({ id: trendTitle }),
          lineSourceIcon: '',
          lineSourceName,
        };
    }
  }, [locale, metricSegVal, pageId, mainSegment, entityType, deviceRealVal, isHourlyMode, trendTitle, lineSourceName]);

  return (
    <div className="dbi-trends-card dbi-kpi-trends-card">
      <CardSlotV2
        className={classNames(
          'dbi-mobileGames-card-context',
          'dbi-mobileGames-title-custom',
          className,
        )}
        title={
          cardTitleProp
            ? cardTitleProp
            : formatMessage({ id: 'dbi_detail_trend' })
        }
        titleStyle={{ lineHeight: '20px' }}
        activeTab={chartType}
        alwaysShowIconList
        iconListExtra={hasVisualMetricValue && exportTableCom}
        iconList={
          // hour 模式默认就是折线图，无需图表切换 icon；非 hour 模式且满足条件下显示折线图+饼图切换
          !isHourlyMode && showDeviceBtnEffective && !showStatusCard
            ? chartIconList
            : []
        }
        getChange={(value: any) => {
          setChartType?.(value);
          onChartTypeChange?.(value);
        }}
      >
        <div className="dbi-kpi-trends-metrics-operate">
          {/* 多选 metrics 修改为单选 */}
          {rightDom}

          {/* By Device -> By Platform：选中 By Country 时置灰 */}
          {showDeviceBtnEffective && (
            <ChartConsumer>
              {(themeContextValue) => {
                const { ifResetLegend, setIfResetLegend } = themeContextValue;
                return (
                  <Checkbox
                    value={byDevice}
                    disabled={byCountry}
                    onChange={(value) => {
                      setIfResetLegend(!ifResetLegend);
                      changeDevice?.(value);
                    }}
                  >
                    {formatMessage({ id: `dbi_by_platform` })}
                  </Checkbox>
                );
              }}
            </ChartConsumer>
          )}
          {/* By Country —— Steam 指标不展示，选中 By Platform 时置灰 */}
          {showByCountryBtnEffective && (
            <ChartConsumer>
              {(themeContextValue) => {
                const { ifResetLegend, setIfResetLegend } = themeContextValue;
                return (
                  <Checkbox
                    value={byCountry}
                    disabled={byDevice}
                    onChange={(value) => {
                      setIfResetLegend(!ifResetLegend);
                      handleByCountryChange?.(value);
                    }}
                  >
                    {formatMessage({ id: `dbi_by_country` })}
                  </Checkbox>
                );
              }}
            </ChartConsumer>
          )}
          {(hasDatabrainData || hasAlineaData) && !showStatusCard && (
            <ShowAllTrueData
              style={{ marginLeft: 'auto' }}
              gameId={gameId}
              gameName={gameName}
              regionsCountries={regionsCountries || []}
              feedBackParams={feedBackParams}
            />
          )}
        </div>
        {showStatusCard ? (
          <CardStats
            cardStatus={cardStatus}
            useDom={true}
            noDataMessage={formatMessage({
              id: 'dbi_info_allMetrics_nodata_msg_new',
            })}
            isDropdown={isDropdown}
          />
        ) : (
          <>
            <div className={chartWrapperClass}>
              <Visualis
                chartHeight={isDualMetricLineChart ? 330 : 350}
                chartNodataMsg={chartNodataMsg}
                tableData={tableData}
                byDevice={deviceRealVal}
                byCountry={byCountry}
                chartType={chartType}
                dateType={dateType}
                startDate={startDate}
                endDate={endDate}
                imageType={entityType}
                // barData={deviceRealVal ? stackBarData : barData}
                lineData={deviceRealVal ? stackLineData : lineData}
                pieData={byCountry ? pieData : (deviceRealVal ? stackPieData : pieData)}
                isNodata={isNoData}
                chartLoading={chartLoading}
                setChartLoading={setChartLoading}
                marketLineLoading={marketLineLoading}
                ifUseMarketLineLoading={ifUseMarketLineLoading}
                topChartsType={pageId}
                titleNow={visualisTitleNow}
                baseReleaseTime={baseReleaseTime}
                earliestPeriod={earliestPeriod}
                // barNameListFormatter={pageCfg?.barNameListFormatter}
                lineSeriesFormatter={
                  deviceRealVal
                    ? pageCfg?.stackLineSeriesFormatter
                    : pageCfg?.lineSeriesFormatter
                }
                // barSeriesFormatter={
                //   deviceRealVal ? pageCfg?.stackBarSeriesFormatter : pageCfg?.barSeriesFormatter
                // }
                pieSeriesFormatter={
                  byCountry
                    ? pageCfg?.pieSeriesFormatter
                    : (deviceRealVal ? pageCfg?.stackPieSeriesFormatter : pageCfg?.pieSeriesFormatter)
                }
                lineMetricName={pageCfg?.lineMetricName}
                visualisLineTopChartsType={pageCfg?.visualisLineTopChartsType}
                visualisBarTopChartsType={pageCfg?.visualisBarTopChartsType}
                visualisPieTopChartsType={pageCfg?.visualisPieTopChartsType}
                isStepLine={isStepLine}
                isCountriesPage={isCountriesPage}
                gameName={gameName}
                calOtherList={calOtherList}
                isStackLine={chartType === ChartType.StackedLine}
                regionsCountries={regionsCountries || []}
                visualMetricValue={visualMetricValue}
                visualMetricValues={visualMetricValues}
                alineaTrueDataSeries={alineaTrueDataSeries}
                tableMetricList={tableMetricList}
                legendSelected={legendSelected}
                ifLineChartLegendOn={ifLineChartLegendOn}
                secondTitleRight={bubbleObjCur.secondTitleRight}
                lineSourceIcon={bubbleObjCur.lineSourceIcon}
                lineSourceName={bubbleObjCur.lineSourceName}
                gridConfig={chartGridConfig}
                enableOpinionEventAnnotation={enableOpinionEventAnnotation}
                controlledEventAnnotation={controlledEventAnnotation}
                opinionEventConfig={opinionEventConfig}
              />
            </div>
            <DynamicColTable
              entityType={entityType}
              gameName={gameName}
              gameCover={gameCover}
              tableData={tableData || []}
              visualMetricValue={visualMetricValue}
              setVisualMetricValue={setVisualMetricValue}
              isManuallyTriggerMetricSelectRef={
                isManuallyTriggerMetricSelectRef
              }
              sumAvgList={sumAvgList}
              fixedTableData={fixedTableData}
              dateType={dateType}
              startDate={startDate}
              endDate={endDate}
              pageSortAttr={pageSortAttr}
              columnList={tableMetricList}
              firstCol={pageCfg?.firstCol}
              specialCol={pageCfg?.specialCol}
              getMetricValueOfCvm={pageCfg?.getMetricValueOfCvm}
              growthHasPoint={pageCfg?.growthHasPoint}
              pageSizeOptions={pageCfg?.pageSizeOptions}
              defaultPageSize={
                pageCfg?.defaultPageSize
                  ? pageCfg?.defaultPageSize
                  : pageCfg?.getDefaultPageSize?.(dateType)
              }
              tableSortMetricProperty={pageCfg?.tableSortMetricProperty}
              tableSortGrowthProperty={pageCfg?.tableSortGrowthProperty}
              regionsCountries={regionsCountries || []}
              onMetricExplanationClick={onMetricExplanationClick}
            />
          </>
        )}
      </CardSlotV2>
    </div>
  );
};
export default TrendsVisualTableContent;
